import { Http } from '@/lib/http';
import { URL_PATHS } from './constants';

export class CollateralManagementService extends Http {
  constructor() {
    super();
    this.endpoint = 'loan-pro/app/loan-services/manage-loans'
  }

  createCollateral(collateral) {
    return this.httpClient().post(`${this.endpoint}/collaterals`, collateral)
  }

  /**
   * 
   * @param {Number} page Pagination age number 
   * @param {Number} limit Items per page
   * @param {String} order The sorting order
   * @param {String} sort What value should be used for sorting 
   * @param {String} search Search parameter
   * @param {String} type The collateral type to search for 
   * @param {Object} isForeclosed Whether to take foreclosure into consideration when searching
   * @returns 
   */
  async getCollaterals(page, limit, order='asc', sort='dateCreated', search, type, isForeclosed) {
    let queryString = [];
    if(page) {
      queryString.push(`page=${page}`)
    }
    if(limit) {
      queryString.push(`limit=${limit}`)
    }
    if(search) {
      queryString.push(`searchParam=${search}`);
    }
    if(sort) {
      queryString.push(`sort=${sort}`);
    }
    if(type) {
      queryString.push(`type=${type}`);
    }
    if(isForeclosed?.use) {
      queryString.push(`isForeclosed=${isForeclosed.value}`);
    }
    queryString.push(`order=${order}`);
    return this.httpClient().get(`${this.endpoint}/collaterals?${queryString.join('&')}`)
  }

  getCollateral(id) {
    return this.httpClient().get(`${this.endpoint}/collaterals/${id}`)
  }

  updateCollateral(id, updates) {
    return this.httpClient().patch(`${this.endpoint}/collaterals/${id}`, updates)
  }

  deleteCollateral(id) {
    return this.httpClient().delete(`${this.endpoint}/collaterals/${id}`)
  }

  createCollateralOwner(owner, ownerCollateralId) {
    return this.httpClient().post(`${this.endpoint}/asset-owners/${ownerCollateralId || 'null'}`, owner);
  }

  updateCollateralOwner(id, updates) {
    return this.httpClient().patch(`${this.endpoint}/asset-owners/${id}`, updates);
  }

  deleteCollateralOwner(id) {
    return this.httpClient().delete(`${this.endpoint}/asset-owners/${id}`);
  }

  addOwnerToCollateral(collateralId, ownerId, owner) {
    return this.httpClient().patch(`${this.endpoint}/asset-owners/collateral/${ownerId}/${collateralId}`, owner)
  }

  removeOwnerFromCollateral(collateralId, ownerId, owner) {
    return this.httpClient().delete(`${this.endpoint}/asset-owners/collateral/${ownerId}/${collateralId}`, owner)
  }

  saveRiskFactorScoring(collateralId, payload) {
    return this.httpClient().post(`${this.endpoint}/collaterals/${collateralId}/risk-score`, payload)
  }
  
  /**
   * Search for pledged or collaterals to be pledged
   * @param payload - The `payload` parameter is used to filter the collateral
   * @example
   * const payload = {
   *   loanId: string - Loan id
   *   accountNumber?: string - the account number to which the policy belongs
   *   search?: string - a search string containing trn or name of collateral owner 
   * }
   * @returns {
   *   data: Array<Object> - collaterals
   *   total: number - total number of collateral for the given filter
   *}
   */
  findLoanCollateral(page = 1, limit = 25, payload) {
    return this.httpClient().post(
      `${URL_PATHS.LOANPRO_APP}/loans/collaterals/search?page=${page}&limit=${limit}`,
      payload
    );
  }

  /**
   * Pledge an insured amount to a loan
   * @param loanId - The id of the loan the collateral is to be pledged to 
   * @param collateralId - The id of the collateral 
   * @param payload - The `payload` parameter used to create the pledged amount record
   * @example
   * const payload = {
   *   loanId: string - Loan id
   *   collateralId: string - collateral id policy
   *   pledgeAmount: number - amount pledged to loan
   * }
   * @returns Object - pledged collateral
   */
  pledgeCollateralToLoan(loanId, collateralId, payload) {
    return this.httpClient().patch(
      `${URL_PATHS.LOANPRO_APP}/loans/${loanId}/collaterals/${collateralId}`,
      payload
    );
  }
  
  /**
   * Remove pledged collateral from loan
   * @param loanId - The id of the loan to remove pledged collateral from
   * @param collateralId - The id of the collateral policy to remove from loan.
   *
   * @returns Object - removed pledged collateral
   */
  removeCollateralFromLoan(loanId, collateralId) {
    return this.httpClient().delete(
      `${URL_PATHS.LOANPRO_APP}/loans/${loanId}/collaterals/${collateralId}`
    );
  }

  findCollateralByOwner(page = 1, limit = 25, payload) {
    return this.httpClient().post(`${this.endpoint}/collaterals/search?page=${page}&limit=${limit}`, payload);
  }
}
